// Centralized data for Entrepminds one-pager
import {
  Network,
  Target,
  Workflow,
  Bot,
  TrendingUp,
  Code2,
  Globe,
  MonitorSmartphone,
  Megaphone,
  LineChart,
  Sparkles,
  Search,
  Settings2,
  Rocket,
} from "lucide-react";

export const NAV_LINKS = [
  { id: "home", label: "Home" },
  { id: "process", label: "Process" },
  { id: "services", label: "GrowthOS" },
  { id: "pricing", label: "Pricing" },
  { id: "case-study", label: "Case Study" },
  { id: "contact", label: "Contact" },
];

export const STATS = [
  { value: "5", label: "Stage Framework", suffix: "" },
  { value: "100", label: "Process Automated", suffix: "%" },
  { value: "24", label: "Implementation Loop", suffix: "/7" },
  { value: "1", label: "Operating System", suffix: "" },
];

export const GROWTH_OS = [
  {
    code: "01",
    title: "Connect",
    tagline: "Build visibility, trust & relationships",
    Icon: Network,
    image:
      "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1100&h=1100&q=85",
    imageAlt: "Two professionals shaking hands at a business meeting",
    items: [
      "Brand Positioning & Identity",
      "Social Media Strategy",
      "Personal Branding",
      "Content & Digital PR",
      "Community Building",
    ],
  },
  {
    code: "02",
    title: "Attract",
    tagline: "Generate qualified opportunities",
    Icon: Target,
    image:
      "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1100&h=1100&q=85",
    imageAlt: "Marketing team brainstorming lead-generation strategy",
    items: [
      "Performance Marketing & PPC",
      "SEO & Organic Growth",
      "Paid Advertising Funnels",
      "Lead Generation Systems",
      "Customer Acquisition Campaigns",
    ],
  },
  {
    code: "03",
    title: "Convert",
    tagline: "Transform opportunities into revenue",
    Icon: Workflow,
    image:
      "https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=1100&h=1100&q=85",
    imageAlt: "Developer building a website on dual screens",
    items: [
      "Hybrid Website Development",
      "Custom Software Development",
      "System & LMS Development",
      "CRM Integration & Booking Systems",
      "Sales Process Optimization",
    ],
  },
  {
    code: "04",
    title: "Automate",
    tagline: "Improve efficiency, reduce cost",
    Icon: Bot,
    image:
      "https://images.unsplash.com/photo-1542831371-29b0f74f9713?auto=format&fit=crop&w=1100&h=1100&q=85",
    imageAlt: "Laptop displaying JSON code — automation and API integration",
    items: [
      "AI Assistants & Chatbots",
      "Workflow & CRM Automation",
      "Accounting Integration",
      "Executive Dashboards",
      "Business Intelligence Systems",
    ],
  },
  {
    code: "05",
    title: "Scale",
    tagline: "Build sustainable growth",
    Icon: TrendingUp,
    image:
      "https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=1100&h=1100&q=85",
    imageAlt: "Executive team reviewing growth strategy charts",
    items: [
      "Business Development",
      "Expansion Planning",
      "Strategic Partnerships",
      "Digital Transformation Roadmaps",
      "Performance Optimization",
    ],
  },
];

export const ADDITIONAL_SERVICES = [
  { Icon: Globe, name: "Basic Website / Landing Page", range: "AED 1,500 – 6,000" },
  { Icon: Globe, name: "Hybrid Website Development", range: "AED 5,000 – 50,000+" },
  { Icon: MonitorSmartphone, name: "Hybrid LMS Development", range: "AED 20,000 – 50,000+" },
  { Icon: Settings2, name: "CRM & Automation Systems", range: "AED 5,000 – 40,000+" },
  { Icon: Bot, name: "AI Chatbots & Assistants", range: "AED 5,000 – 50,000+" },
  { Icon: Megaphone, name: "Social Media Marketing", range: "AED 3,000 – 25,000 / mo" },
  { Icon: Sparkles, name: "Media Production (Audio / Video)", range: "from AED 300" },
  { Icon: Code2, name: "Technical Support & Maintenance", range: "AED 2,000 – 10,000 / mo" },
];

export const PROCESS_STEPS = [
  {
    Icon: Search,
    title: "Diagnose",
    desc: "Full business assessment — revenue leaks, ops bottlenecks, tech gaps, automation opportunities.",
  },
  {
    Icon: Settings2,
    title: "Optimize",
    desc: "Customized blueprint to strengthen sales, marketing, customer experience and infrastructure.",
  },
  {
    Icon: Bot,
    title: "Automate",
    desc: "Deploy AI, workflows, dashboards and intelligence systems that run the business 24/7.",
  },
  {
    Icon: Rocket,
    title: "Scale",
    desc: "Expansion playbooks, partnerships and optimization loops that compound growth.",
  },
];

export const PRICING = [
  {
    id: "starter",
    name: "GrowthOS Starter",
    audience: "For Startups & Small Businesses",
    setup: "AED 8,000",
    monthly: "AED 3,000",
    contract: "12 months minimum",
    featured: false,
    features: [
      "Business Diagnostic Assessment",
      "Growth Opportunity Analysis",
      "Cost Optimization Review",
      "CRM Setup",
      "Website Review",
      "Basic Automation Setup",
      "Monthly Consultation",
      "Performance Monitoring",
    ],
  },
  {
    id: "growth",
    name: "GrowthOS Growth",
    audience: "For SMEs & Growing Businesses",
    setup: "AED 15,000",
    monthly: "AED 5,000",
    contract: "12 months minimum",
    featured: true,
    features: [
      "Advanced Business Assessment",
      "Lead Generation Framework",
      "CRM Integration",
      "Website Optimization",
      "AI Chatbot Setup",
      "Customer Journey Mapping",
      "Process Automation",
      "Executive Dashboard & KPI Monitoring",
    ],
  },
  {
    id: "enterprise",
    name: "GrowthOS Enterprise",
    audience: "For Medium & Large Organizations",
    setup: "AED 30,000",
    monthly: "AED 10,000",
    contract: "12 months minimum",
    featured: false,
    features: [
      "Enterprise Growth Audit",
      "Multi-System Integration",
      "Accounting System Integration",
      "Business Intelligence Dashboards",
      "Advanced AI Automation",
      "Department Workflow Optimization",
      "Executive Strategy Sessions",
      "Expansion Planning",
    ],
  },
];

export const SPECIALIST_PACKAGES = [
  "Social Media",
  "Performance (PPC)",
  "Website and WebApp",
  "eCommerce",
  "LMS/CMR/ERP",
  "Automation",
  "AI Agent",
];

export const TESTIMONIALS = [
  {
    quote:
      "Entrepminds didn't hand us a 60-slide deck — they rebuilt our intake, CRM and marketing as one system. Inquiries that used to slip through now convert on autopilot.",
    name: "Lea Ferrer",
    role: "Founder & CEO",
    company: "White Aura International Beauty Academy",
    initials: "WA",
    metric: "+47% enrollment in 90 days",
  },
  {
    quote:
      "The GrowthOS™ executive dashboard finally gave us a single pane of glass over revenue, leads and operations. Decisions that took weeks now take an afternoon.",
    name: "Hassan Khoury",
    role: "Operations Director",
    company: "Levant Wellness Group",
    initials: "LW",
    metric: "3× faster monthly close",
  },
  {
    quote:
      "Performance marketing, automation and a hybrid LMS — built and integrated by one team in one quarter. This is how consulting should work in the AI era.",
    name: "Reem Al-Qassimi",
    role: "Managing Partner",
    company: "Atlas Education Holdings",
    initials: "AE",
    metric: "−38% cost per qualified lead",
  },
];

export const PILOT = {
  name: "White Aura International Beauty Academy",
  subtitle: "First implementation of GrowthOS™",
  image:
    "https://images.pexels.com/photos/7031713/pexels-photo-7031713.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  deliverables: [
    "Brand Positioning",
    "Hybrid Website",
    "Hybrid LMS Platform",
    "CRM System",
    "AI Inquiry System",
    "Social Media Infrastructure",
    "Marketing Automation",
    "Student Journey Automation",
  ],
};

export const ASSETS = {
  logo: "https://customer-assets.emergentagent.com/job_tech-solutions-hub-139/artifacts/4eo289jl_elogo.png",
  heroAbstract:
    "https://customer-assets.emergentagent.com/job_tech-solutions-hub-139/artifacts/gh8l4429_Gemini_Generated_Image_%20%286%29.png",
  dubai:
    "https://images.unsplash.com/photo-1634007626524-f47fa37810a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxEdWJhaSUyMHNreWxpbmUlMjBuaWdodCUyMGx1eHVyeXxlbnwwfHx8fDE3ODA5MjYwMzB8MA&ixlib=rb-4.1.0&q=85",
};

export const CONTACT_INFO = {
  email: "info@entrepmindsconsultancy.ai",
  address: "Business Bay, Dubai, UAE",
};

export const SPARKLE_ICON = Sparkles;
export const LINE_CHART_ICON = LineChart;
